"use client";

import { Search, User, ShoppingCart, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../styles/Header.css";
import { useAuth } from "../auth/AuthContext";
import { useLoggedInUserId } from "../../api/GetLoggedInUserId";
import { useState, useEffect } from "react";
import LoginRequiredModal from "../item/LoginRequiredModal";

export default function Header() {
    const { auth, logout } = useAuth();
    const navigate = useNavigate();
    const { data: memberId, isLoading, isError } = useLoggedInUserId();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // 윈도우 크기 변경 감지
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            if (window.innerWidth > 768) {
                setIsMobileMenuOpen(false);
                setIsSubMenuOpen(false);
            }
        };

        // 다른 곳 클릭 시 모바일 메뉴 닫기
        const handleClickOutside = (event) => {
            const mobileMenu = document.querySelector('.mobile-menu');
            const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
            
            if (mobileMenu && mobileMenuToggle && 
                !mobileMenu.contains(event.target) && 
                !mobileMenuToggle.contains(event.target) &&
                isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
                setIsSubMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    // 장바구니 클릭 핸들러
    const handleCartClick = () => {
        if (!memberId || isLoading || isError) {
            setIsLoginModalOpen(true);
        } else {
            navigate("/cart");
        }
    };

    // 마이페이지 클릭 핸들러
    const handleMyPageClick = () => {
        if (!memberId || isLoading || isError) {
            setIsLoginModalOpen(true);
        } else {
            navigate("/mypage");
        }
    };

    // 모바일 메뉴 토글
    const toggleMobileMenu = () => {
        if (isMobileMenuOpen) {
            setIsSubMenuOpen(false);
        }
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // 메뉴 항목 클릭 시 모바일 메뉴 닫기
    const handleMenuItemClick = (path) => {
        navigate(path);
        setIsMobileMenuOpen(false);
        setIsSubMenuOpen(false);
    };

    return (
        <div className="header-div">
            <header className="header">
                <div className="header-container">
                    <div className="header-content">
                        {/* Logo */}
                        <button onClick={() => navigate("/")} className="logo">
                            <img src="/images/softcat_new_logo2.png" alt="소프트캣 로고" />
                        </button>

                        {/* Desktop Navigation */}
                        <nav className="nav">
                            <button onClick={() => navigate("/about")} className="nav-item">소프트캣 소개</button>
                            <button onClick={() => navigate("/products")} className="nav-item">프로그램 상품</button>
                            <button onClick={() => navigate("/custom-product-first")} className="nav-item">맞춤상품 신청</button>
                            <div className="support">
                                <span className="nav-item">고객지원</span>
                                <div className="support-menu">
                                    <button onClick={() => navigate("/manual")} className="nav-item">동영상 매뉴얼</button>
                                    <button onClick={() => navigate("/service")} className="nav-item">고객센터</button>
                                    <button onClick={() => navigate("/FAQ")} className="nav-item">FAQ</button>
                                </div>
                            </div>
                        </nav>

                        {/* Mobile Menu Toggle Button */}
                        <button 
                            className="mobile-menu-toggle" 
                            aria-label="Toggle mobile menu"
                            onClick={toggleMobileMenu}
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>

                        {/* Right side icons */}
                        <div className="flex icon_menu items-center ml-2 space-x-4">
                            <button className="icon-button" aria-label="Search" onClick={() => navigate("/search")}>
                                <Search className="icon" />
                            </button>
                            <button className="icon-button" aria-label="User profile" onClick={handleMyPageClick}>
                                <User className="icon" />
                            </button>
                            <button className="icon-button" aria-label="Shopping cart" onClick={handleCartClick}>
                                <ShoppingCart className="icon" />
                            </button>

                            {auth.role === "ADMIN" && (
                                <button onClick={() => navigate("/admin")} className="nav-item">관리자 페이지</button>
                            )}

                            {auth.isLoggedIn ? (
                                <button onClick={logout} className="nav-item">로그아웃</button>
                            ) : (
                                <>
                                    <button onClick={() => navigate("/login")} className="nav-item">로그인</button>
                                    <button onClick={() => navigate("/signup")} className="nav-item">회원가입</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu */}
            <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
                <button onClick={() => handleMenuItemClick("/about")} className="nav-item">소프트캣 소개</button>
                <button onClick={() => handleMenuItemClick("/products")} className="nav-item">프로그램 상품</button>
                <button onClick={() => handleMenuItemClick("/custom-product-first")} className="nav-item">맞춤상품 신청</button>
                <div className="support">
                    <button 
                        className={`nav-item ${isSubMenuOpen ? 'open' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            setIsSubMenuOpen(!isSubMenuOpen);
                        }}
                    >
                        고객지원
                    </button>
                    <div className={`support-menu ${isSubMenuOpen ? 'active' : ''}`}>
                        <button onClick={() => handleMenuItemClick("/manual")} className="nav-item">동영상 매뉴얼</button>
                        <button onClick={() => handleMenuItemClick("/service")} className="nav-item">고객센터</button>
                        <button onClick={() => handleMenuItemClick("/FAQ")} className="nav-item">FAQ</button>
                    </div>
                </div>
                
                {auth.role === "ADMIN" && (
                    <button onClick={() => handleMenuItemClick("/admin")} className="nav-item">관리자 페이지</button>
                )}

                {auth.isLoggedIn ? (
                    <button onClick={logout} className="nav-item">로그아웃</button>
                ) : (
                    <>
                        <button onClick={() => handleMenuItemClick("/login")} className="nav-item">로그인</button>
                        <button onClick={() => handleMenuItemClick("/signup")} className="nav-item">회원가입</button>
                    </>
                )}
            </div>

            {/* 로그인 유도 모달 */}
            <LoginRequiredModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onConfirm={() => {
                    window.scrollTo(0, 0);
                    navigate("/login");
                }}
            />
        </div>
    );
}
